"use client";

import * as React from "react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin } from "lucide-react";
function DirectoryListingConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="h-80 rounded-[var(--radius-md)] bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground mb-4">Failed to load directory. Please try again.</p>
        <button onClick={refetch} className="text-sm text-accent underline hover:text-accent-hover transition-colors duration-[var(--duration-fast)]">
          Retry
        </button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-center text-muted-foreground py-12">No listings available at the moment.</p>;
  }
  return <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
      {products.map((product, i) => <article key={product.id} data-index={i} className="group">
          <Card className="border border-border overflow-hidden transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] hover:-translate-y-1 hover:shadow-medium">
            <Link to={product.link}>
              {product.featured_image_url && <div className="aspect-square w-full overflow-hidden bg-muted">
                  <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:scale-102" loading="lazy" />
                </div>}
              <CardHeader className="gap-2 pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base font-display font-semibold leading-tight tracking-tight line-clamp-2">
                    {product.title}
                  </CardTitle>
                  {product.price === 0 && <Badge variant="secondary" className="shrink-0 text-xs font-sans">
                      FREE
                    </Badge>}
                </div>
                {product.categories && product.categories.length > 0 && <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="size-3" />
                    <span className="truncate">{product.categories[0]}</span>
                  </div>}
              </CardHeader>
            </Link>
            <CardContent className="pt-0">
              {product.short_description && <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                  {product.short_description}
                </p>}
              <div className="flex items-center justify-between gap-3">
                {product.price !== undefined && product.price > 0 && <div className="text-sm font-display font-semibold text-foreground">
                    ${product.price.toFixed(2)}
                  </div>}
                <AddToCartButton productId={product.id} variant="link" className="w-full" buttonClassName="bg-primary text-foreground-primary" text="View Details" showQuantity={false} />
              </div>
            </CardContent>
          </Card>
        </article>)}
    </div>;
}
export default function DirectoryListing() {
  return <section id="directory-listing" className="dark bg-background text-foreground py-16 md:py-24 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight leading-tight mb-4">
            Free France Directory
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Discover free museums, parks, city tours, and hidden gems across France.<br />
            Experience French culture without spending a euro.
          </p>
        </div>
        <EcommerceProductsProvider postType="product" className="w-full" wp_query={{
        per_page: 12,
        order: "asc",
        orderby: "title",
        product_cat: [17]
      }} fields={["id", "title.rendered", "short_description", "price", "categories", "featured_image_url", "link"]}>
          <DirectoryListingConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}